import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { apiClient } from "@/services/apiClient";
import { authService } from "../services";
import { DASHBOARD_ROUTES, AUTH_ROUTES } from "../constants";
import { getPrimaryRole, normalizeRoles } from "../utils";
import type {
  LoginRequest,
  EnterpriseSignupRequest,
  SessionResponse,
  UserInfoResponse,
  PasswordResetConfirmDto,
} from "../types";
import type { UserRole } from "@/types/user.types";

/**
 * Get the dashboard route for a given role
 */
function getDashboardRoute(role: UserRole): string {
  return DASHBOARD_ROUTES[role];
}

/**
 * Hook for login mutation
 */
export function useLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const queryClient = useQueryClient();

  return useMutation({
    // LoginForm renders the error inline (Alert) — skip the global error toast.
    meta: { suppressErrorToast: true },
    mutationFn: async (data: LoginRequest) => {
      // Get tokens from login
      const tokens = await authService.login(data);

      // Store tokens in localStorage
      localStorage.setItem("tr_access_token", tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem("tr_refresh_token", tokens.refresh_token);
      }

      // Fetch user session with the new token
      const session = await apiClient.get<SessionResponse>("/auth/session", undefined, {
        headers: {
          Authorization: `Bearer ${tokens.access_token}`,
        },
      });

      // /auth/session only carries roles — fetch /auth/me for the REAL
      // permission strings (CHAT_MODERATE, CALENDAR_CREATE_EVENT, ...).
      // Permission-gated UI (useHasPermission) breaks without them.
      const userInfo = await apiClient.get<UserInfoResponse>("/auth/me", undefined, {
        headers: {
          Authorization: `Bearer ${tokens.access_token}`,
        },
      });

      return { session, userInfo };
    },
    onSuccess: ({ session, userInfo }) => {
      // Primary role drives the landing dashboard; the full set drives access checks.
      const role = getPrimaryRole(session.roles);
      const roles = normalizeRoles(session.roles);

      // Build user object from session + user info responses
      const user = {
        id: session.userId,
        email: session.email,
        name: session.email.split("@")[0] || "User",
        role,
        roles,
        authType: userInfo.authType,
        permissions: userInfo.permissions,
        emailVerified: userInfo.emailVerified,
        createdAt: userInfo.createdAt,
      };

      login(user);
      queryClient.clear();

      // Navigate to the original destination or role-based dashboard
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
      const destination = from ?? getDashboardRoute(user.role);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(destination, { replace: true });
    },
  });
}

/**
 * Hook for logout mutation
 */
export function useLogout() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const queryClient = useQueryClient();

  return useMutation({
    // Logout recovers locally on failure (onError below) — no error toast.
    meta: { suppressErrorToast: true },
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Clear tokens from localStorage
      localStorage.removeItem("tr_access_token");
      localStorage.removeItem("tr_refresh_token");
      // Clear OAuth session keys
      sessionStorage.removeItem("tr_oauth_code_verifier");
      sessionStorage.removeItem("tr_oauth_return_to");
      logout();
      queryClient.clear();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(AUTH_ROUTES.LOGIN, { replace: true });
    },
    onError: () => {
      // Even if the API call fails, clear local state
      localStorage.removeItem("tr_access_token");
      localStorage.removeItem("tr_refresh_token");
      // Clear OAuth session keys
      sessionStorage.removeItem("tr_oauth_code_verifier");
      sessionStorage.removeItem("tr_oauth_return_to");
      logout();
      queryClient.clear();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(AUTH_ROUTES.LOGIN, { replace: true });
    },
  });
}

/**
 * Hook for enterprise registration mutation
 * Simplified to just email/password
 */
export function useRegister() {
  const navigate = useNavigate();

  return useMutation({
    // EnterpriseSignupForm renders the error inline — skip the global error toast.
    meta: { suppressErrorToast: true },
    mutationFn: (data: EnterpriseSignupRequest) => authService.register(data),
    onSuccess: () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(AUTH_ROUTES.PENDING_APPROVAL, { replace: true });
    },
  });
}

/**
 * Hook for forgot password mutation
 */
export function useForgotPassword() {
  return useMutation({
    // ForgotPasswordForm renders the error inline — skip the global error toast.
    meta: { suppressErrorToast: true },
    mutationFn: (email: string) => authService.forgotPassword(email),
  });
}

/**
 * Hook for reset password mutation
 */
export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    // ResetPasswordPage renders the error inline — skip the global error toast.
    meta: { suppressErrorToast: true },
    mutationFn: (data: PasswordResetConfirmDto) => authService.resetPassword(data),
    onSuccess: () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(AUTH_ROUTES.LOGIN, { replace: true });
    },
  });
}

/**
 * Hook for email verification mutation
 */
export function useVerifyEmail() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (token: string) => authService.verifyEmail(token),
    onSuccess: () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(AUTH_ROUTES.LOGIN, { replace: true });
    },
  });
}

/**
 * Hook for resending verification email
 * Rate limited to 3 requests per hour
 */
export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) => authService.resendVerification(email),
  });
}

/**
 * Hook for fetching current user info
 */
export function useCurrentUser() {
  const login = useAuthStore((s) => s.login);

  return useMutation({
    mutationFn: () => authService.getCurrentUser(),
    onSuccess: (userInfo) => {
      // Primary role drives the landing dashboard; the full set drives access checks.
      const role = getPrimaryRole(userInfo.roles);
      const roles = normalizeRoles(userInfo.roles);

      const user = {
        id: userInfo.id,
        email: userInfo.email,
        name: userInfo.email.split("@")[0] || "User",
        role,
        roles,
        authType: userInfo.authType,
        permissions: userInfo.permissions,
        emailVerified: userInfo.emailVerified,
        createdAt: userInfo.createdAt,
      };

      login(user);
    },
  });
}
